// The console's sign-in stage (owner design approved 2026-10-26): root at mission control. A panel of
// Nepal (the official map, nepalMap.ts) lights up shop pins one by one at real city positions (the
// pins are an illustration, not live data); root waves "Namaste!", then
// presents the owner's tools one board at a time (Vendors, Plans & Wallet, Import Marketplace,
// Services, Platform health, Security & support) and waves goodbye before the loop starts again.
// root is a still render of the real 3D model (tools/mascot, Waving and Talking poses). The stage is
// laid out at 640 × 640 and scaled to its box. Reduced motion: no slides or pulses. Screen readers
// get the tools as a list.
import { t } from "@ady/i18n";
import { cx, Icon, type IconName } from "@ady/ui";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import rootPresent from "./assets/root-present.png";
import rootWave from "./assets/root-wave.png";
import { NEPAL_MAP, placeOnMap } from "./nepalMap";

type ToolKey = "vendors" | "money" | "import" | "services" | "health" | "security";
const TOOLS: { key: ToolKey; icon: IconName }[] = [
  { key: "vendors", icon: "store" },
  { key: "money", icon: "wallet" },
  { key: "import", icon: "ship" },
  { key: "services", icon: "megaphone" },
  { key: "health", icon: "pulse" },
  { key: "security", icon: "shieldCheck" },
];

/** Shop pins at real city positions (longitude, latitude). */
const CITIES = (
  [
    ["Kathmandu", 85.324, 27.717],
    ["Pokhara", 83.985, 28.209],
    ["Biratnagar", 87.272, 26.455],
    ["Birgunj", 84.877, 27.012],
    ["Butwal", 83.448, 27.7],
    ["Dharan", 87.284, 26.812],
    ["Nepalgunj", 81.616, 28.05],
    ["Dhangadhi", 80.59, 28.694],
    ["Janakpur", 85.925, 26.729],
    ["Bharatpur", 84.433, 27.683],
    ["Hetauda", 85.032, 27.428],
    ["Damak", 87.703, 26.659],
  ] as const
).map(([city, lon, lat]) => ({ city, ...placeOnMap(lon, lat) }));

const INTRO = 3400;
const EACH = 4000;
const OUTRO = 2800;
const PIN_EVERY = 450;
const LOOP = INTRO + TOOLS.length * EACH + OUTRO;
const STAGE = 640;
/** root's left edge: with the hello / goodbye bubble (root and bubble centred), and beside the boards. */
const ROOT_LEFT_GREET = 112;
const ROOT_LEFT_TOOLS = 40;

export function MissionControl() {
  const box = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState({ w: 0, h: 0 });
  useLayoutEffect(() => {
    const el = box.current;
    if (!el) return;
    const measure = () => setSize({ w: el.clientWidth, h: el.clientHeight });
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // One loop clock, a few ticks a second (pins light every 450 ms, boards change every 4 s).
  const [elapsed, setElapsed] = useState(0);
  useEffect(() => {
    const t0 = performance.now();
    const id = window.setInterval(() => setElapsed((performance.now() - t0) % LOOP), 200);
    return () => window.clearInterval(id);
  }, []);

  const inTools = elapsed >= INTRO && elapsed < INTRO + TOOLS.length * EACH;
  const outro = elapsed >= INTRO + TOOLS.length * EACH;
  const active = inTools ? Math.floor((elapsed - INTRO) / EACH) : -1;
  const lit = Math.min(CITIES.length, Math.floor(elapsed / PIN_EVERY));
  const s = size.w ? Math.min(size.w / STAGE, size.h / STAGE) : 0;

  return (
    <div ref={box} className="absolute inset-0 overflow-hidden">
      <ul className="sr-only">
        {TOOLS.map((tool) => (
          <li key={tool.key}>{t(`console.mission.tools.${tool.key}.title`)}</li>
        ))}
      </ul>
      {s ? (
        <div
          aria-hidden="true"
          className="absolute top-0 origin-top-left"
          style={{
            left: Math.round((size.w - STAGE * s) / 2),
            width: STAGE,
            height: STAGE,
            transform: `scale(${s})`,
          }}
        >
          {/* Shops across Nepal */}
          <div
            className="absolute flex flex-col rounded-panel border border-card bg-surface/90 px-4.5 py-3.5 shadow-card"
            style={{ left: 32, top: 28, width: 576, height: 236 }}
          >
            <div className="flex items-center justify-between">
              <span className="text-sm font-semibold text-fg">{t("console.mission.map")}</span>
              <span className="inline-flex h-5.5 items-center gap-1.5 rounded-full bg-success-bg px-2 text-2xs font-bold tracking-wider text-success-fg uppercase">
                <span className="size-1.5 rounded-full bg-success-fg motion-safe:animate-pulse" />
                {t("console.mission.live")}
              </span>
            </div>
            <div
              className="absolute"
              style={{ left: 123, top: 36, width: NEPAL_MAP.width, height: NEPAL_MAP.height }}
            >
              <svg
                width={NEPAL_MAP.width}
                height={NEPAL_MAP.height}
                viewBox={`0 0 ${NEPAL_MAP.width} ${NEPAL_MAP.height}`}
                className="absolute inset-0 overflow-visible"
              >
                <defs>
                  <linearGradient id="console-nepal-fill" x1="0" y1="0" x2="1" y2="1">
                    <stop
                      offset="0"
                      style={{ stopColor: "var(--ady-color-brand-blue)", stopOpacity: 0.16 }}
                    />
                    <stop
                      offset="1"
                      style={{ stopColor: "var(--ady-color-brand-green)", stopOpacity: 0.16 }}
                    />
                  </linearGradient>
                </defs>
                <path d={NEPAL_MAP.outline} fill="url(#console-nepal-fill)" />
                {NEPAL_MAP.provinces.map((d, i) => (
                  <path
                    key={i}
                    d={d}
                    fill="none"
                    strokeWidth={1.4}
                    strokeLinejoin="round"
                    style={{ stroke: "var(--ady-color-brand-blue)", strokeOpacity: 0.35 }}
                  />
                ))}
                <path
                  d={NEPAL_MAP.outline}
                  fill="none"
                  strokeWidth={1.4}
                  strokeLinejoin="round"
                  style={{ stroke: "var(--ady-color-brand-blue)", strokeOpacity: 0.75 }}
                />
              </svg>
              {CITIES.map((c, i) => {
                const newest = i === lit - 1 && !outro;
                return (
                  <span
                    key={c.city}
                    className="absolute size-0 transition-opacity duration-500"
                    style={{ left: c.x, top: c.y, opacity: i < lit ? 1 : 0 }}
                  >
                    {newest ? (
                      <span className="absolute -top-1.5 -left-1.5 size-3 rounded-full bg-primary/45 motion-safe:animate-ping" />
                    ) : null}
                    <span className="ady-bg-brand absolute -top-[5px] -left-[5px] size-2.5 rounded-full border-2 border-surface shadow-1" />
                    {newest ? (
                      <span className="absolute -top-2.5 left-2.5 rounded-full border border-card bg-surface px-2 text-2xs font-semibold whitespace-nowrap text-fg shadow-1">
                        {c.city}
                      </span>
                    ) : null}
                  </span>
                );
              })}
            </div>
          </div>

          {/* The owner's tools, one board at a time */}
          {TOOLS.map((tool, i) => (
            <div
              key={tool.key}
              className="absolute flex flex-col gap-2.5 rounded-panel border border-card bg-surface p-4.5 shadow-3 transition-[transform,opacity] ease-out motion-reduce:transition-none"
              style={{
                left: 290,
                top: 322,
                width: 312,
                opacity: i === active ? 1 : 0,
                transform: `translateX(${i === active ? 0 : i < active ? -28 : 28}px)`,
                // The old board leaves quickly, the new one arrives just after: never two at once.
                transitionDuration: i === active ? "450ms" : "220ms",
                transitionDelay: i === active ? "200ms" : "0ms",
              }}
            >
              <div className="flex items-center gap-3">
                <span className="ady-bg-brand-tint flex size-10.5 flex-none items-center justify-center rounded-xl">
                  <Icon name={tool.icon} gradient />
                </span>
                <span className="flex flex-col">
                  <span className="text-2xs font-bold tracking-widest text-link uppercase">
                    {t("console.mission.toolOf", { n: i + 1, total: TOOLS.length })}
                  </span>
                  <span className="text-lg leading-tight font-bold text-fg">
                    {t(`console.mission.tools.${tool.key}.title`)}
                  </span>
                </span>
              </div>
              <ul className="flex flex-col gap-1.5">
                {(["p1", "p2", "p3"] as const).map((pt) => (
                  <li key={pt} className="flex items-start gap-2 text-sm text-fg">
                    <Icon name="check" className="mt-0.5 flex-none text-success-fg" />
                    <span>{t(`console.mission.tools.${tool.key}.${pt}`)}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}

          {/* root on a soft floor glow: centred with his bubble for hello and goodbye, steps left for the boards */}
          <div
            className="pointer-events-none absolute transition-[left] duration-700 ease-out motion-reduce:transition-none"
            style={{
              left: inTools ? ROOT_LEFT_TOOLS : ROOT_LEFT_GREET,
              top: 286,
              width: 186,
              height: 248,
            }}
          >
            <span
              className="ady-login-floor absolute"
              style={{ left: 8, top: 222, width: 170, height: 30 }}
            />
            <img
              src={inTools ? rootPresent : rootWave}
              alt=""
              className="absolute inset-0 size-full"
            />
          </div>

          {/* Hi and goodbye, beside root's head */}
          {!inTools ? (
            <div
              className="absolute flex items-center"
              style={{ left: ROOT_LEFT_GREET + 192, top: 348, height: 56 }}
            >
              <p
                key={outro ? "bye" : "hello"}
                className="ady-root-greet ady-console-bubble relative w-max max-w-64 rounded-lg border border-card bg-surface px-3.5 py-2.5 text-sm font-semibold text-fg shadow-card"
              >
                {outro ? t("console.mission.bye") : t("console.mission.hello")}
              </p>
            </div>
          ) : null}

          {/* Which tool */}
          <div
            className="absolute inset-x-0 flex flex-col items-center gap-2"
            style={{ bottom: 18 }}
          >
            <div className="flex items-center gap-1.5">
              {TOOLS.map((tool, i) => (
                <span
                  key={tool.key}
                  className={cx(
                    "h-1.5 rounded-full transition-all duration-300",
                    i === active ? "w-5 bg-primary" : "w-1.5 bg-primary/25",
                  )}
                />
              ))}
            </div>
            <span className="text-xs font-medium text-fg-secondary">
              {active >= 0
                ? t(`console.mission.tools.${TOOLS[active]!.key}.title`)
                : t("console.mission.meet")}
            </span>
          </div>
        </div>
      ) : null}
    </div>
  );
}
